/**
 * One row, opened out.
 *
 * The grid shows a row as a line of cells the width of their columns; this
 * shows the same row as a list of fields with room around them. It exists for
 * three reasons and only one of them is comfort: a wide table has fields the
 * grid can only reach by scrolling sideways, a long text field is unreadable
 * in a cell, and three of the field types still without an editor -- files,
 * collaborators, a link to another table -- need a control larger than a cell,
 * which is a thing this dialog can hold and the grid cannot.
 *
 * **Same editing model as the grid: one editor at a time.** A field shows the
 * text it reads as until it is opened, and opening it puts the editor there.
 * A form of twenty always-mounted controls is the other way to build this, and
 * it would need a draft per field, twenty subscriptions, and an answer to
 * which of twenty controls takes the focus when the dialog opens. One at a
 * time reuses the grid's answer to all three, and reuses its editors unchanged
 * -- each of them expects to be the only one on screen, and here it is.
 *
 * **The discussion and the record of what happened are beside it**, as two
 * tabs, because both are about the row rather than about any one field and
 * neither is worth half the width of the dialog on its own. They fold away for
 * a row whose fields want the whole width. Both are asked for only once the row
 * itself has arrived: a link to a row somebody has deleted would otherwise ask
 * for the comments on a row that is not there.
 *
 * **The fields the view hides are here too, behind a disclosure.** They are the
 * reason a row can be opened out at all and still not show what somebody is
 * looking for, and this dialog is the only place in the application that can
 * show a hidden field's value without changing the view for everybody who uses
 * it. Which fields the *view* shows stays where that decision already lives --
 * the column menu -- so there is one control for it rather than two.
 */
import { useRef, useState } from 'react'
import {
  ChevronDown,
  ChevronUp,
  EyeOff,
  PanelRightClose,
  PanelRightOpen,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import type { Field, RowData } from '@/api/types'
import type { FieldContext } from '../fields/behavior/behavior'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/button'
import { Skeleton } from '@/shared/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { CommentPanel } from './comment-panel'
import { HistoryPanel } from './history-panel'
import { editorFor } from '../fields/ui/edit'
import { cellText, cellValue } from '../fields/read'
import { CellDisplay } from '../fields/ui/read'
import { commitCellEdit } from '../rows/rowEdit'
import type { RowWindowMember } from '../rows/rowWindowRegistry'

export function RowModal({
  member,
  rowId,
  row,
  missing,
  fields,
  hidden,
  context,
  onClose,
  onStep,
  steps,
}: {
  member: RowWindowMember
  /** The row the address names, which is known before the row itself is. */
  rowId: number
  /** Null while a row the grid is not holding is on its way. */
  row: RowData | null
  /** True once it is settled that there is no such row to show. */
  missing: boolean
  /** In the order the view puts them, and only the ones it shows. */
  fields: readonly Field[]
  /** The rest, in the same order, shown only when asked for. */
  hidden: readonly Field[]
  /** The lookups the fields need that are not on the row. */
  context: FieldContext
  onClose: () => void
  onStep: (previous: boolean) => void
  /** Whether there is known to be a row either side of this one. */
  steps: { previous: boolean; next: boolean }
}) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState<{ fieldId: number; draft: string } | null>(null)
  // A way of looking at one row rather than a setting, so it is not stored
  // anywhere: it lasts as long as the dialog does, which includes stepping from
  // one row to the next, and starts closed again on the next row somebody opens.
  const [revealed, setRevealed] = useState(false)
  // Whether the discussion and the record of what happened are beside the
  // fields. Open is the useful default -- the dialog is the only place either
  // can be read -- but a row whose fields are wide, a link column especially,
  // is worth the whole width, so it folds away and is not stored either.
  const [aside, setAside] = useState(true)
  // Focused to close an editor, the way the grid focuses itself: losing focus
  // is what commits, so there has to be somewhere for the focus to go that is
  // not the editor and not outside the dialog.
  const list = useRef<HTMLDivElement>(null)

  // What is being edited, readable *now* rather than at the next render.
  //
  // Closing an editor means moving the focus off it, and moving the focus runs
  // the commit synchronously, inside the same handler that has just asked for
  // the editor to close. A `useState` value does not change until React
  // re-renders, so the commit would still see the edit it was told to
  // abandon and would send it. The grid has the same arrangement and does not
  // need this, because its editing state is a store and a store is written
  // when it is set.
  const current = useRef(editing)
  current.current = editing

  const primary = fields.find((field) => field.primary)
  const heading =
    primary === undefined || row === null ? '' : cellText(primary, row, context).trim()

  function open(field: Field): void {
    const editor = editorFor(field)
    if (editor === null || row === null) return
    setEditing({ fieldId: field.id, draft: editor.toDraft(field, cellValue(field, row)) })
  }

  function commit(): void {
    const editing = current.current
    if (editing === null) return
    current.current = null
    setEditing(null)

    // Both lists: a hidden field that has been revealed is edited the same way
    // as any other, and looking only in the shown ones would drop what was
    // typed into it without a word.
    const field = [...fields, ...hidden].find(
      (candidate) => candidate.id === editing.fieldId,
    )
    if (field === undefined || row === null) return

    void commitCellEdit(member, row, field, editing.draft, context)?.catch(() => {
      toast.error(t('grid.editFailed'))
    })
  }

  /** Abandons the open editor without sending what was typed. */
  function abandon(): void {
    current.current = null
    setEditing(null)
    list.current?.focus()
  }

  function onEditorKeyDown(event: React.KeyboardEvent<HTMLElement>): void {
    if (event.key === 'Enter') {
      event.preventDefault()
      // Focusing the list blurs the editor, and blurring the editor is what
      // commits it. Committing here as well would be a second route to the
      // same place that no test could tell from the first.
      list.current?.focus()
    }
  }

  // At the head of the second column rather than in the dialog's header: it
  // belongs to the panel it folds away, and collapsed it is all that is left of
  // it -- the rule between the fields and the panel stays, so the dialog does
  // not change shape around it.
  const asideToggle = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="shrink-0"
      aria-pressed={aside}
      aria-label={t(aside ? 'row.hideAside' : 'row.showAside')}
      onClick={() => {
        setAside((shown) => !shown)
      }}
    >
      {aside ? (
        <PanelRightClose className="size-4" />
      ) : (
        <PanelRightOpen className="size-4" />
      )}
    </Button>
  )

  const fieldRow = (field: Field, at: RowData) => (
    <FieldRow
      key={field.id}
      field={field}
      row={at}
      context={context}
      draft={editing?.fieldId === field.id ? editing.draft : null}
      onOpen={() => {
        open(field)
      }}
      onType={(draft) => {
        setEditing((was) => (was === null ? was : { ...was, draft }))
      }}
      onCommit={commit}
      onKeyDown={onEditorKeyDown}
    />
  )

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        className="sm:max-w-6xl"
        // Escape closes the editor when one is open and the dialog otherwise.
        // Taken here rather than in the editor's own key handler because the
        // dialog listens for Escape on the document and in the capture phase,
        // so by the time a handler on the input sees the key the dialog has
        // already closed -- taking the editor with it, and committing the edit
        // that was being abandoned, because unmounting a focused box is
        // losing focus and losing focus is what commits.
        onEscapeKeyDown={(event) => {
          if (current.current === null) return
          event.preventDefault()
          abandon()
        }}
        // Nothing to restore the focus to: the button that opens a row is out
        // of the tab order, so the caller puts the focus back on the grid
        // itself and Radix must not put it anywhere else afterwards.
        onCloseAutoFocus={(event) => {
          event.preventDefault()
        }}
      >
        <DialogHeader className="border-b pr-16 pb-4">
          <div className="flex items-center gap-2">
            {/* Before the title rather than beside the close button, because
                stepping through rows is the thing somebody does repeatedly and
                the pointer should not have to move to keep doing it. */}
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="size-7 shrink-0 on-surface"
              disabled={!steps.previous}
              aria-label={t('row.previous')}
              onClick={() => {
                onStep(true)
              }}
            >
              <ChevronUp className="size-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="size-7 shrink-0 on-surface"
              disabled={!steps.next}
              aria-label={t('row.next')}
              onClick={() => {
                onStep(false)
              }}
            >
              <ChevronDown className="size-4" />
            </Button>
            <DialogTitle className="truncate">
              {heading === '' ? t('row.heading', { id: rowId }) : heading}
            </DialogTitle>
          </div>
          <DialogDescription>{t('row.heading', { id: rowId })}</DialogDescription>
        </DialogHeader>

        <div
          className={cn(
            'grid gap-4',
            // Collapsed, the second column is only as wide as the button that
            // brings it back, so the fields take the rest and the rule between
            // them stays where it was.
            row !== null &&
              (aside
                ? 'sm:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]'
                : 'sm:grid-cols-[minmax(0,1fr)_auto]'),
          )}
        >
          <div
            ref={list}
            tabIndex={-1}
            className="-mx-2 max-h-[60vh] overflow-y-auto px-2 outline-none"
          >
            {row === null ? (
              // A dialog that opens empty and fills in, rather than a table
              // that sits still until a row nobody can see has arrived.
              missing ? (
                <p className="py-6 text-sm text-muted-foreground">{t('row.missing')}</p>
              ) : (
                <div className="space-y-3 py-2">
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-2/3" />
                </div>
              )
            ) : (
              <>
                {fields.map((field) => fieldRow(field, row))}

                {hidden.length > 0 && (
                  <>
                    {/* Between the two lists rather than above them both, so that
                      what it reveals appears where it says it will. */}
                    <button
                      type="button"
                      aria-expanded={revealed}
                      onClick={() => {
                        // Whatever is open is committed first, by the same route
                        // everything else here commits: the editor loses the
                        // focus to this button. Nothing to do but flip it.
                        setRevealed((at) => !at)
                      }}
                      className="mt-2 flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted/40"
                    >
                      <EyeOff className="size-4" />
                      {t(revealed ? 'row.hideHidden' : 'row.showHidden', {
                        count: hidden.length,
                      })}
                    </button>
                    {revealed && hidden.map((field) => fieldRow(field, row))}
                  </>
                )}
              </>
            )}
          </div>

          {row !== null && !aside && (
            <div className="border-t pt-4 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-4">
              {asideToggle}
            </div>
          )}
          {row !== null && aside && (
            <Tabs
              defaultValue="comments"
              className="flex max-h-[60vh] min-h-0 flex-col gap-3 border-t pt-4 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-4"
            >
              <div className="flex items-center gap-3">
                {asideToggle}
                <TabsList className="flex-1">
                  <TabsTrigger value="comments">{t('comment.plural')}</TabsTrigger>
                  <TabsTrigger value="history">{t('history.plural')}</TabsTrigger>
                </TabsList>
              </div>
              {/* Each panel is mounted only while its tab is showing, which is
                  what decides whether it asks: opening a row fetches the
                  discussion, and the record of what happened costs nothing
                  until somebody looks at it. */}
              <TabsContent value="comments" className="flex min-h-0 flex-col">
                <CommentPanel tableId={member.tableId} rowId={rowId} />
              </TabsContent>
              <TabsContent value="history" className="flex min-h-0 flex-col">
                <HistoryPanel
                  tableId={member.tableId}
                  rowId={rowId}
                  // Both lists: the record is about the row, and a change to a
                  // field this view happens to hide is still something that
                  // happened to it.
                  fields={[...fields, ...hidden]}
                  context={context}
                />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Whether an element is a file input, whose blur means a picker opened. */
const isFilePicker = (target: EventTarget | null): boolean =>
  target instanceof HTMLInputElement && target.type === 'file'

/**
 * One field: its name, and either what it says or the editor for it.
 *
 * A field with no editor is drawn as text with nothing to press. Read-only is
 * not the same as uninteresting -- a formula's result is often the reason
 * somebody opened the row -- so it reads the same as every other field and
 * simply does not open.
 */
function FieldRow({
  field,
  row,
  context,
  draft,
  onOpen,
  onType,
  onCommit,
  onKeyDown,
}: {
  field: Field
  row: RowData
  context: FieldContext
  /** What has been typed, or null when this field is not the one being edited. */
  draft: string | null
  onOpen: () => void
  onType: (draft: string) => void
  onCommit: () => void
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void
}) {
  const { t } = useTranslation()
  const frame = useRef<HTMLDivElement>(null)
  const editor = editorFor(field)
  const text = cellText(field, row, context)

  return (
    <div className="grid grid-cols-[10rem_1fr] items-start gap-3 border-b py-2 last:border-b-0">
      <span className="truncate pt-1.5 text-sm text-muted-foreground" title={field.name}>
        {field.name}
      </span>

      {draft !== null && editor !== null ? (
        <div
          ref={frame}
          data-testid="row-field-editor"
          // The same rule the grid uses: focus leaving the *frame* commits,
          // not a control blurring, because an editor may be more than one
          // focusable thing.
          onBlur={(event) => {
            if (frame.current?.contains(event.relatedTarget) === true) return
            // A file input blurs when its picker opens and takes the focus
            // back when the picker closes -- which may be a minute later, and
            // with nothing in `relatedTarget`, because the focus did not go
            // anywhere on the page. Committing there would tear the editor
            // down while somebody was still choosing a file, and the file they
            // chose would land nowhere.
            //
            // Only here and not in the grid's editor layer, which can never
            // hold one of these: an editor with a file input needs more room
            // than a cell, so the grid opens the row instead.
            if (event.relatedTarget === null && isFilePicker(event.target)) return
            onCommit()
          }}
          className="min-h-8"
        >
          <editor.Component
            field={field}
            context={context}
            // One field is opened at a time here too, by pressing it.
            autoFocus
            value={draft}
            label={t('grid.editing', { field: field.name })}
            onValueChange={onType}
            onKeyDown={onKeyDown}
          />
        </div>
      ) : editor === null ? (
        <span className="min-h-8 py-1.5 text-sm break-words whitespace-pre-wrap">
          <CellDisplay field={field} row={row} context={context} wrap />
        </span>
      ) : (
        <button
          type="button"
          onClick={onOpen}
          className={cn(
            'min-h-8 rounded border border-transparent px-2 py-1.5 text-left text-sm break-words whitespace-pre-wrap hover:border-input hover:bg-muted/40',
            text === '' && 'text-muted-foreground',
          )}
        >
          {/* The text decides whether the cell is empty even where something
              else draws it: it is the same rendering a filter matches against,
              so "reads as nothing" and "matches nothing" cannot disagree. */}
          {text === '' ? (
            t('row.empty')
          ) : (
            <CellDisplay field={field} row={row} context={context} wrap />
          )}
        </button>
      )}
    </div>
  )
}
